import type { ReactNode } from "react";
import { useLocation } from "react-router";

import { AdminVNextShell } from "@/vnext/app-shell/AdminVNextShell";
import { useWorkspaceChromeLogging } from "@/vnext/routes/useWorkspaceChromeLogging";
import {
  NO_WORKSPACE_TABS,
  useAdminNavPageTabStrip,
} from "@/vnext/shared/workspace/useAdminNavPageTabStrip";
import { WorkspaceTabNav } from "@/vnext/shared/workspace/WorkspaceTabNav";
import "@/vnext/shared/workspace/workspaceChrome.css";

interface InsightsWorkspaceChromeProps {
  actions?: ReactNode;
  children: ReactNode;
  subtitle: string;
  title: string;
}

export function InsightsWorkspaceChrome({
  actions,
  children,
  subtitle,
  title,
}: InsightsWorkspaceChromeProps) {
  const location = useLocation();
  // §11 page tabs (e.g. Business metrics, App usage); the Insights workspace
  // itself has in-page tabs only, so it shows none here.
  const strip = useAdminNavPageTabStrip(location.pathname);
  const logTabClick = useWorkspaceChromeLogging({
    activePath: strip?.activePath ?? "",
    componentName: "InsightsWorkspaceChrome",
    pathname: location.pathname,
    tabs: strip?.tabs ?? NO_WORKSPACE_TABS,
    workspaceLabel: "insights",
  });

  return (
    <AdminVNextShell
      actions={actions}
      breadcrumbs={[
        { label: "Dashboard", path: "/dashboard" },
        { label: "Insights" },
        { label: title },
      ]}
      subtitle={subtitle}
      title={title}
    >
      <div className="admin-workspace-page">
        {strip ? (
          <WorkspaceTabNav
            activePath={strip.activePath}
            ariaLabel={strip.ariaLabel}
            onTabClick={logTabClick}
            tabs={strip.tabs}
          />
        ) : null}
        {children}
      </div>
    </AdminVNextShell>
  );
}
